// Defensive patches for Node.prototype.removeChild / insertBefore.
//
// Chrome's auto-translate (and some extensions) re-parent React-owned text
// nodes into <font> wrappers behind React's back. React 18's reconciler then
// calls removeChild/insertBefore against a child that is no longer where the
// virtual DOM says it is, the DOM throws NotFoundError, and the component
// tree crashes. On /checkout this crash-loops: Sentry NMH-REACT-34N puts
// /checkout as the worst-affected page in the app (~51 buyers/30d), and a
// session replay confirmed a real buyer for whom the page never loaded
// (OMG-2136).
//
// This is the industry-standard mitigation from facebook/react#11538: when
// the child's actual parent isn't the node being operated on, log and no-op
// instead of throwing. All legitimate operations delegate to the originals.
//
// We log at console.error (not warn): our Sentry captureConsoleIntegration
// only captures `levels: ["error"]`, so warn would never reach Sentry — the
// guard would fire invisibly and NMH-REACT-34N would go silent once this
// ships, with no signal for the degraded-UI mode (a no-op'd insertBefore
// silently drops the new node — on /checkout that could be an unrendered
// error message). error surfaces the same signal as a non-fatal message.
//
// Marker carried by each installed wrapper so a repeat install (module
// re-evaluation, future second call site) can't stack a second layer of
// wrapping. It lives on the function rather than in module state so tests
// that restore the original prototypes can re-install cleanly.
const GUARD_MARKER = "__omgyesDomNodeGuard";
type MaybeGuarded = { [GUARD_MARKER]?: true };

export const installDomNodeGuards = (): void => {
  if (typeof Node !== "function" || !Node.prototype) return;
  if ((Node.prototype.removeChild as MaybeGuarded)[GUARD_MARKER]) return;

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function <T extends Node>(
    this: Node,
    child: T,
  ): T {
    if (child.parentNode !== this) {
      console.error(
        "domNodeGuards: skipped removeChild for a re-parented node (translator/extension DOM mutation?)",
        child,
      );
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };
  (Node.prototype.removeChild as MaybeGuarded)[GUARD_MARKER] = true;

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function <T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null,
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      console.error(
        "domNodeGuards: skipped insertBefore for a re-parented reference node (translator/extension DOM mutation?)",
        referenceNode,
      );
      return newNode;
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };
  (Node.prototype.insertBefore as MaybeGuarded)[GUARD_MARKER] = true;
};
